import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface AppShellProps {
  /** Slot del Sidebar (desktop). No se renderiza en mobile. */
  sidebar: ReactNode;
  isNarrow: boolean;
  /** true si hay un banner fijo (trial/impersonación/demo) que empuja el contenido hacia abajo. */
  hasTopBanner: boolean;
  /** className final para <main>, ya resuelto por el layout padre (idéntico al que usa el shell actual). */
  mainClassName: string;
  mainStyle?: CSSProperties;
  children: ReactNode;
  /** SCRUM-252: sidebar colapsado a modo ícono-solo (68px en vez de 256px). */
  collapsed?: boolean;
}

/**
 * AppShell del Design System (F1/F2/F3) — layout de sidebar fijo + contenido.
 * Primer consumidor: /app/dashboard. El resto de las rutas sigue usando el shell
 * actual de app.tsx hasta que se migren (mismo patrón evolutivo del Experience Engine).
 */
export function AppShell({ sidebar, isNarrow, hasTopBanner, mainClassName, mainStyle, children, collapsed = false }: AppShellProps) {
  return (
    <>
      {!isNarrow && (
        <aside
          data-sidebar="primary"
          data-sidebar-collapsed={collapsed ? "true" : "false"}
          className={cn(
            "fixed left-0 z-40 hidden flex-col border-r border-sidebar-border bg-sidebar py-3 transition-[width] duration-200 lg:flex",
            collapsed ? "w-[68px]" : "w-64",
            hasTopBanner ? "top-10 h-[calc(100vh-2.5rem)]" : "top-0 h-screen",
          )}
        >
          {sidebar}
        </aside>
      )}
      <main className={mainClassName} style={mainStyle}>
        {children}
      </main>
    </>
  );
}
